/**
 * Lead detail page (Phase 3B).
 *
 * Shows everything about one lead — contact, budget, pipeline state,
 * linked listings, and the person's timeline. Lifecycle actions (edit,
 * delete) plus quick logging.
 *
 * The timeline belongs to the person, not the deal: for a lead with a contact
 * it is the same `ContactTimeline` the Messages conversation shows, loaded
 * through React Query, with entries from the person's other leads included.
 * A call or message logged here is a message on the person tagged with this
 * lead; notes, reminders and viewings stay activities on the lead. A lead
 * whose contact was deleted shows only its own activities, and every type it
 * logs is an activity on the lead, since there is no person to log against.
 */

import { useQueryClient } from "@tanstack/react-query";
import {
	ArrowLeft,
	Edit,
	MessageSquareReply,
	Phone,
	Plus,
	Trash2,
} from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
	NextStepPrompt,
	type NextStepTarget,
} from "@/features/realtor/components/follow-ups/next-step-prompt";
import {
	ContactTimeline,
	TimelineView,
} from "@/features/realtor/components/timeline/contact-timeline";
import { LogReplyDialog } from "@/features/realtor/components/timeline/log-reply-dialog";
import {
	useContact,
	useInvalidateConversation,
	useLogContactMessage,
} from "@/features/realtor/hooks/use-realtor-crm";
import {
	type ActivityType,
	createActivity,
	deleteActivity,
	deleteLead,
	getMyLead,
	type Lead,
	type LeadActivity,
	listLeadActivities,
	type PipelineStage,
} from "@/features/realtor/services/leads-api.service";
import type { Listing } from "@/features/realtor/services/listings-api.service";
import type {
	MessageChannel,
	MessageDirection,
	TimelineEntry,
} from "@/features/realtor/services/realtor-crm-api.service";
import { errorMessage } from "@/features/realtor/utils/error-message";
import { formatPaiseAsRupees } from "@/features/realtor/utils/listings-format";
import { Pill, StudioButton } from "@/features/studio/theme";
import { queryKeys } from "@/lib/query-keys";

const ACTIVITY_TYPES: ActivityType[] = [
	"call",
	"viewing",
	"message",
	"note",
	"reminder",
];
const DIRECTIONS: readonly MessageDirection[] = ["outbound", "inbound"];
// A call is logged on the call channel; a message picks any other channel.
const MESSAGE_CHANNELS: readonly MessageChannel[] = [
	"whatsapp",
	"sms",
	"email",
	"in_person",
	"other",
];
const TIMELINE_BOX = "max-h-[32rem] rounded-md";

type LoadState =
	| { status: "loading" }
	| { status: "error"; message: string }
	| {
			status: "loaded";
			lead: Lead;
			listings: Listing[];
			activities: LeadActivity[];
	  };

export function LeadDetailPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { id } = useParams<{ id: string }>();
	const [load, setLoad] = useState<LoadState>({ status: "loading" });
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showActivityDialog, setShowActivityDialog] = useState(false);
	const [loggingReply, setLoggingReply] = useState(false);
	const invalidateConversation = useInvalidateConversation();
	const contactId = load.status === "loaded" ? load.lead.contactId : null;
	// The person's own name, for their side of the timeline. The lead's name
	// stands in until it loads.
	const contact = useContact(contactId);
	const ownActivities = useMemo(
		() =>
			load.status === "loaded"
				? load.activities.map(activityEntry).sort(byOccurredAt)
				: [],
		[load],
	);

	const runLoad = useCallback(
		async (cancelledRef: { cancelled: boolean }) => {
			if (!id) {
				if (!cancelledRef.cancelled) {
					setLoad({
						status: "error",
						message: t("realtor.leads.detail.missingId"),
					});
				}
				return;
			}
			setLoad({ status: "loading" });
			const leadResult = await getMyLead(id);
			if (cancelledRef.cancelled) return;
			if (!leadResult.success || !leadResult.data) {
				setLoad({
					status: "error",
					message: leadResult.error ?? t("realtor.leads.loadFailedFallback"),
				});
				return;
			}
			// A lead with a contact shows the person's timeline, which loads
			// itself. Only a lead without one needs its own activity list.
			if (leadResult.data.lead.contactId) {
				setLoad({
					status: "loaded",
					lead: leadResult.data.lead,
					listings: leadResult.data.listings,
					activities: [],
				});
				return;
			}
			const activitiesResult = await listLeadActivities(id);
			if (cancelledRef.cancelled) return;
			// Activities load is best-effort — the lead itself rendered
			// fine, so we keep the page usable. But surface the failure
			// so the realtor knows their journal didn't load (and won't
			// re-log a call that's already in there).
			if (!activitiesResult.success) {
				toast.error(
					activitiesResult.error ??
						t("realtor.leads.detail.activitiesLoadFailed"),
				);
			}
			setLoad({
				status: "loaded",
				lead: leadResult.data.lead,
				listings: leadResult.data.listings,
				activities: activitiesResult.success
					? (activitiesResult.data ?? [])
					: [],
			});
		},
		[id, t],
	);

	useEffect(() => {
		const ref = { cancelled: false };
		runLoad(ref);
		return () => {
			ref.cancelled = true;
		};
	}, [runLoad]);

	if (load.status === "loading") {
		return (
			<div className="bg-dashboard-surface min-h-screen p-6 space-y-6">
				<div className="space-y-3">
					<Skeleton className="h-6 w-32" />
					<Skeleton className="h-12 w-full" />
					<Skeleton className="h-48 w-full" />
					<Skeleton className="h-32 w-full" />
				</div>
			</div>
		);
	}

	if (load.status === "error") {
		return (
			<div className="bg-dashboard-surface min-h-screen p-6">
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-6 text-center space-y-4">
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14,
								color: "var(--color-dashboard-muted)",
							}}
						>
							{load.message}
						</p>
						<StudioButton onClick={() => navigate("/realtor/leads")}>
							{t("realtor.leads.backToList")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	const { lead, listings } = load;
	// Home's schedule (Visits & calls, Coming up, and the Scheduled visits and
	// Reminders due tiles) is cached for minutes, and this page is where its
	// activities are created and deleted. Marking the cached entries stale makes
	// Home fetch them again the next time it opens.
	const invalidateSchedule = () => {
		queryClient.invalidateQueries({ queryKey: queryKeys.realtor.schedule() });
	};

	// Functional updaters: a slow refresh-then-action sequence might
	// otherwise stamp the stale {lead, listings} captured at handler-
	// creation back into state, dropping any newer fetch that landed
	// in between.
	const handleActivityCreated = (activity: LeadActivity) => {
		if (lead.contactId) {
			void invalidateConversation(lead.contactId);
			return;
		}
		setLoad((current) =>
			current.status === "loaded"
				? { ...current, activities: [activity, ...current.activities] }
				: current,
		);
		invalidateSchedule();
	};

	// An entry on the person's timeline may belong to another of their leads,
	// so the delete goes to the lead the entry names.
	const handleActivityDelete = async (entry: TimelineEntry) => {
		const owner = entry.lead?.id ?? lead.id;
		const result = await deleteActivity(owner, entry.id);
		if (!result.success) {
			toast.error(
				result.error ?? t("realtor.leads.detail.deleteActivityFailed"),
			);
			return;
		}
		if (lead.contactId) {
			void invalidateConversation(lead.contactId);
		} else {
			setLoad((current) =>
				current.status === "loaded"
					? {
							...current,
							activities: current.activities.filter((a) => a.id !== entry.id),
						}
					: current,
			);
			invalidateSchedule();
		}
		toast.success(t("realtor.leads.detail.activityDeletedToast"));
	};
	const contactName = contact.data?.contact.name ?? lead.name;
	return (
		<div className="bg-dashboard-surface min-h-screen p-6 space-y-6">
			<button
				type="button"
				onClick={() => navigate("/realtor/leads")}
				className="inline-flex items-center gap-1.5 text-sm hover:opacity-70 transition-opacity"
				style={{ color: "var(--color-dashboard-muted)" }}
			>
				<ArrowLeft className="h-4 w-4" />
				<span style={{ fontFamily: "var(--font-body)" }}>
					{t("realtor.leads.backToList")}
				</span>
			</button>

			<header className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 11,
							fontWeight: 600,
							letterSpacing: "0.18em",
							textTransform: "uppercase",
							color: "var(--color-dashboard-muted)",
						}}
					>
						{t(`realtor.leads.types.${lead.type}`)} ·{" "}
						{t(`realtor.leads.stages.${lead.pipelineStage}`)}
					</p>
					<h1
						style={{
							fontFamily: "var(--font-display)",
							fontSize: 32,
							fontWeight: 500,
							fontStyle: "italic",
							color: "var(--color-dashboard-text)",
							marginTop: 8,
							lineHeight: 1.1,
						}}
						className="truncate"
					>
						{lead.name}
					</h1>
				</div>
				<div className="flex gap-2">
					{/* ph-no-capture, not data-sensitive: the number is in the
					    href, and maskTextSelector reaches text nodes only —
					    an attribute is recorded verbatim. See lib/posthog.ts. */}
					<a
						href={`tel:${lead.phoneCountryCode}${lead.phone}`}
						className="ph-no-capture inline-flex items-center justify-center rounded-md border h-9 w-9 hover:bg-dashboard-surface transition-colors"
						style={{
							borderColor: "var(--color-dashboard-border)",
							backgroundColor: "var(--color-dashboard-bg)",
						}}
						aria-label={t("realtor.leads.callAriaLabel", { name: lead.name })}
					>
						<Phone
							className="h-4 w-4"
							style={{ color: "var(--color-dashboard-text)" }}
						/>
					</a>
					<StudioButton
						variant="secondary"
						size="sm"
						icon={Edit}
						onClick={() => navigate(`/realtor/leads/${lead.id}/edit`)}
					>
						{t("realtor.leads.detail.edit")}
					</StudioButton>
					<StudioButton
						variant="secondary"
						size="sm"
						icon={Trash2}
						onClick={() => setConfirmDelete(true)}
					>
						{t("realtor.leads.detail.delete")}
					</StudioButton>
				</div>
			</header>

			<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-6 space-y-4">
					{/* A lead is a third party who never saw the recording
					    toggle, so their contact details are masked. */}
					<KeyValue
						sensitive
						label={t("realtor.leads.detail.phone")}
						value={`+${lead.phoneCountryCode} ${lead.phone}`}
					/>
					{lead.email && (
						<KeyValue
							sensitive
							label={t("realtor.leads.detail.email")}
							value={lead.email}
						/>
					)}
					{lead.source && (
						<KeyValue
							label={t("realtor.leads.detail.source")}
							value={lead.source}
						/>
					)}
					{(lead.budgetMin != null || lead.budgetMax != null) && (
						<KeyValue
							label={t("realtor.leads.detail.budget")}
							value={formatBudget(lead.budgetMin, lead.budgetMax)}
						/>
					)}
					<KeyValue
						label={t("realtor.leads.detail.temperature")}
						value={
							<Pill tone="neutral" dot>
								{lead.temperature}
							</Pill>
						}
					/>
					{lead.tags.length > 0 && (
						<div>
							<p
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 11,
									fontWeight: 600,
									letterSpacing: "0.18em",
									textTransform: "uppercase",
									color: "var(--color-dashboard-muted)",
									marginBottom: 8,
								}}
							>
								{t("realtor.leads.detail.tags")}
							</p>
							<div className="flex flex-wrap gap-1.5">
								{lead.tags.map((tag) => (
									<Pill key={tag} tone="neutral">
										{tag}
									</Pill>
								))}
							</div>
						</div>
					)}
					{lead.notes && (
						<div
							style={{
								borderTop: "1px solid var(--color-dashboard-border)",
								paddingTop: 12,
							}}
						>
							<p
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 11,
									fontWeight: 600,
									letterSpacing: "0.18em",
									textTransform: "uppercase",
									color: "var(--color-dashboard-muted)",
									marginBottom: 8,
								}}
							>
								{t("realtor.leads.detail.notes")}
							</p>
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-text)",
									whiteSpace: "pre-line",
								}}
							>
								{lead.notes}
							</p>
						</div>
					)}
				</CardContent>
			</Card>

			{listings.length > 0 && (
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-6 space-y-3">
						<h2
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 11,
								fontWeight: 600,
								letterSpacing: "0.18em",
								textTransform: "uppercase",
								color: "var(--color-dashboard-muted)",
							}}
						>
							{t("realtor.leads.detail.linkedListings")}
						</h2>
						<div className="space-y-2">
							{listings.map((listing) => (
								<button
									key={listing.id}
									type="button"
									onClick={() => navigate(`/realtor/listings/${listing.id}`)}
									className="w-full text-left rounded-md border p-4 hover:bg-dashboard-surface transition-colors"
									style={{
										borderColor: "var(--color-dashboard-border)",
										backgroundColor: "var(--color-dashboard-paper)",
									}}
								>
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 14,
											fontWeight: 500,
											color: "var(--color-dashboard-text)",
										}}
										className="truncate"
									>
										{listing.headline}
									</p>
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 12,
											color: "var(--color-dashboard-muted)",
										}}
									>
										{listing.locality} · {listing.city} ·{" "}
										{formatPaiseAsRupees(listing.askingPrice)}
									</p>
								</button>
							))}
						</div>
					</CardContent>
				</Card>
			)}

			<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-6 space-y-4">
					<div className="flex flex-wrap items-center justify-between gap-3">
						<h2
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 11,
								fontWeight: 600,
								letterSpacing: "0.18em",
								textTransform: "uppercase",
								color: "var(--color-dashboard-muted)",
							}}
						>
							{t("realtor.leads.detail.activities")}
						</h2>
						<div className="flex flex-wrap justify-end gap-2">
							{lead.contactId && (
								<StudioButton
									variant="secondary"
									size="sm"
									icon={MessageSquareReply}
									onClick={() => setLoggingReply(true)}
								>
									{t("realtor.timeline.logReply.action")}
								</StudioButton>
							)}
							<StudioButton
								size="sm"
								icon={Plus}
								onClick={() => setShowActivityDialog(true)}
							>
								{t("realtor.leads.detail.logActivity")}
							</StudioButton>
						</div>
					</div>
					{lead.contactId ? (
						<ContactTimeline
							contactId={lead.contactId}
							contactName={contactName}
							emptyText={t("realtor.leads.detail.noActivities")}
							onDeleteActivity={(entry) => void handleActivityDelete(entry)}
							className={TIMELINE_BOX}
						/>
					) : (
						<TimelineView
							entries={ownActivities}
							contactName={lead.name}
							emptyText={t("realtor.leads.detail.noActivities")}
							onDeleteActivity={(entry) => void handleActivityDelete(entry)}
							className={TIMELINE_BOX}
						/>
					)}
				</CardContent>
			</Card>

			<Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
				<DialogContent
					style={{
						backgroundColor: "var(--color-dashboard-bg)",
						border: "1px solid var(--color-dashboard-border)",
					}}
				>
					<DialogHeader>
						<DialogTitle
							style={{
								fontFamily: "var(--font-display)",
								fontSize: 20,
								fontWeight: 500,
								fontStyle: "italic",
								color: "var(--color-dashboard-text)",
							}}
						>
							{t("realtor.leads.detail.deleteConfirmTitle")}
						</DialogTitle>
						<DialogDescription
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14,
								color: "var(--color-dashboard-muted)",
							}}
						>
							{t("realtor.leads.detail.deleteConfirmBody")}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<StudioButton
							variant="secondary"
							disabled={isDeleting}
							onClick={() => setConfirmDelete(false)}
						>
							{t("realtor.leads.detail.cancel")}
						</StudioButton>
						<StudioButton
							variant="primary"
							disabled={isDeleting}
							onClick={async () => {
								if (isDeleting) return;
								setIsDeleting(true);
								const result = await deleteLead(lead.id);
								setIsDeleting(false);
								if (!result.success) {
									toast.error(
										result.error ??
											t("realtor.leads.detail.deleteFailFallback"),
									);
									return;
								}
								setConfirmDelete(false);
								toast.success(t("realtor.leads.detail.deletedToast"));
								invalidateSchedule();
								// Its activities leave the person's timeline with it.
								if (lead.contactId) {
									void invalidateConversation(lead.contactId);
								}
								navigate("/realtor/leads");
							}}
						>
							{t("realtor.leads.detail.delete")}
						</StudioButton>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<LogActivityDialog
				open={showActivityDialog}
				onOpenChange={setShowActivityDialog}
				leadId={lead.id}
				leadStage={lead.pipelineStage}
				contactId={lead.contactId}
				personName={contactName}
				onCreated={handleActivityCreated}
			/>

			{lead.contactId && (
				<LogReplyDialog
					open={loggingReply}
					onOpenChange={setLoggingReply}
					contactId={lead.contactId}
					contactName={contactName}
					leadId={lead.id}
				/>
			)}
		</div>
	);
}

// ----------------------------------------------------------------------
// The lead's own activities as timeline entries (lead without a contact)
// ----------------------------------------------------------------------

function activityEntry(activity: LeadActivity): TimelineEntry {
	return {
		kind: "activity",
		id: activity.id,
		// The same rule the server's timeline uses for an activity's time.
		occurredAt:
			activity.completedAt ?? activity.scheduledAt ?? activity.createdAt,
		createdAt: activity.createdAt,
		direction: null,
		channel: null,
		activityType: activity.type,
		title: activity.title,
		body: activity.body,
		lead: null,
		source: "manual",
		importOrdinal: null,
		scheduledAt: activity.scheduledAt,
		completedAt: activity.completedAt,
	};
}

function byOccurredAt(a: TimelineEntry, b: TimelineEntry): number {
	return (
		a.occurredAt.localeCompare(b.occurredAt) ||
		a.createdAt.localeCompare(b.createdAt)
	);
}

// ----------------------------------------------------------------------
// Log activity dialog
// ----------------------------------------------------------------------

interface LogActivityDialogProps {
	open: boolean;
	onOpenChange: (next: boolean) => void;
	leadId: string;
	leadStage: PipelineStage;
	/** The lead's person. With one, calls and messages are logged on them. */
	contactId: string | null;
	/** Who "When should you follow up next?" names. */
	personName: string;
	onCreated: (activity: LeadActivity) => void;
}

function LogActivityDialog({
	open,
	onOpenChange,
	leadId,
	leadStage,
	contactId,
	personName,
	onCreated,
}: LogActivityDialogProps) {
	const { t } = useTranslation();
	const ids = useId();
	const logMessage = useLogContactMessage();
	const [type, setType] = useState<ActivityType>("call");
	const [title, setTitle] = useState("");
	const [body, setBody] = useState("");
	const [scheduledAt, setScheduledAt] = useState("");
	const [direction, setDirection] = useState<MessageDirection>("outbound");
	const [channel, setChannel] = useState<MessageChannel>("whatsapp");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [nextStep, setNextStep] = useState<NextStepTarget | null>(null);
	const closeNextStep = useCallback(() => setNextStep(null), []);
	// KTD2: with a person to log against, a call or message is a conversation
	// entry on them, not an activity on the lead.
	const asMessage =
		contactId !== null && (type === "call" || type === "message");

	const reset = () => {
		setType("call");
		setTitle("");
		setBody("");
		setScheduledAt("");
		setDirection("outbound");
		setChannel("whatsapp");
	};

	const submitMessage = async (personId: string) => {
		if (!body.trim()) {
			toast.error(t("realtor.leads.detail.whatWasSaidRequired"));
			return;
		}
		setIsSubmitting(true);
		try {
			await logMessage.mutateAsync({
				contactId: personId,
				input: {
					direction,
					channel: type === "call" ? "call" : channel,
					body: body.trim(),
					leadId,
				},
			});
			toast.success(t("realtor.leads.detail.conversationLoggedToast"));
			reset();
			onOpenChange(false);
			setNextStep({
				contactId: personId,
				contactName: personName,
				fromLeadId: leadId,
			});
		} catch (error: unknown) {
			toast.error(
				errorMessage(error, t("realtor.leads.detail.logActivityFailed")),
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleSubmit = async () => {
		if (asMessage && contactId) {
			await submitMessage(contactId);
			return;
		}
		if (!title.trim()) {
			toast.error(t("realtor.leads.detail.activityTitleRequired"));
			return;
		}
		setIsSubmitting(true);
		// Reminder/viewing → scheduled-future. Other types → completed-now
		// (so they show up in the journal immediately).
		const isScheduled = type === "reminder" || type === "viewing";
		const result = await createActivity(leadId, {
			type,
			title: title.trim(),
			body: body.trim() || null,
			scheduledAt:
				isScheduled && scheduledAt ? new Date(scheduledAt).toISOString() : null,
			completedAt: isScheduled ? null : new Date().toISOString(),
		});
		setIsSubmitting(false);
		if (!result.success || !result.data) {
			toast.error(result.error ?? t("realtor.leads.detail.logActivityFailed"));
			return;
		}
		onCreated(result.data);
		toast.success(t("realtor.leads.detail.activityLoggedToast"));
		reset();
		onOpenChange(false);
		// A call or message on a lead with no person is still a conversation.
		if (type === "call" || type === "message") {
			setNextStep({
				contactId: null,
				contactName: personName,
				fromLeadId: leadId,
				leads: [{ id: leadId, pipelineStage: leadStage }],
			});
		}
	};

	return (
		<>
			<Dialog
				open={open}
				onOpenChange={(next) => {
					onOpenChange(next);
					if (!next) reset();
				}}
			>
				<DialogContent
					style={{
						backgroundColor: "var(--color-dashboard-bg)",
						border: "1px solid var(--color-dashboard-border)",
					}}
				>
					<DialogHeader>
						<DialogTitle
							style={{
								fontFamily: "var(--font-display)",
								fontSize: 20,
								fontWeight: 500,
								fontStyle: "italic",
								color: "var(--color-dashboard-text)",
							}}
						>
							{t("realtor.leads.detail.logActivityTitle")}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<div className="space-y-1.5">
							<Label
								htmlFor="activity-type"
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 11,
									fontWeight: 600,
									letterSpacing: "0.18em",
									textTransform: "uppercase",
									color: "var(--color-dashboard-muted)",
								}}
							>
								{t("realtor.leads.detail.activityType")}
							</Label>
							<Select
								value={type}
								onValueChange={(v) => setType(v as ActivityType)}
							>
								<SelectTrigger
									id="activity-type"
									style={{
										backgroundColor: "var(--color-dashboard-paper)",
										borderColor: "var(--color-dashboard-border)",
										color: "var(--color-dashboard-text)",
									}}
								>
									<SelectValue />
								</SelectTrigger>
								<SelectContent
									style={{
										backgroundColor: "var(--color-dashboard-bg)",
										borderColor: "var(--color-dashboard-border)",
									}}
								>
									{ACTIVITY_TYPES.map((activityType) => (
										<SelectItem key={activityType} value={activityType}>
											{t(`realtor.leads.activityTypes.${activityType}`)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						{asMessage && (
							<div className="flex flex-wrap items-end gap-3">
								<fieldset className="min-w-0">
									<legend className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-dashboard-muted">
										{t("realtor.messages.composer.directionLabel")}
									</legend>
									<div className="inline-flex rounded-lg border border-dashboard-border bg-dashboard-paper p-0.5">
										{DIRECTIONS.map((value) => (
											<button
												key={value}
												type="button"
												aria-pressed={direction === value}
												onClick={() => setDirection(value)}
												className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
													direction === value
														? "bg-dashboard-accent text-dashboard-on-fill"
														: "text-dashboard-muted hover:text-dashboard-text"
												}`}
											>
												{t(`realtor.messages.composer.${value}`)}
											</button>
										))}
									</div>
								</fieldset>
								{type === "message" && (
									<div className="min-w-0">
										<label
											htmlFor={`${ids}-channel`}
											className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-dashboard-muted"
										>
											{t("realtor.messages.composer.channelLabel")}
										</label>
										<select
											id={`${ids}-channel`}
											value={channel}
											onChange={(event) =>
												setChannel(event.target.value as MessageChannel)
											}
											className="h-9 rounded-lg border border-dashboard-border bg-dashboard-paper px-2 text-sm text-dashboard-text"
										>
											{MESSAGE_CHANNELS.map((value) => (
												<option key={value} value={value}>
													{t(`realtor.messages.channels.${value}`)}
												</option>
											))}
										</select>
									</div>
								)}
							</div>
						)}
						{asMessage && (
							<p className="text-[11px] text-dashboard-muted">
								{t("realtor.leads.detail.conversationHint")}
							</p>
						)}
						{!asMessage && (
							<div className="space-y-1.5">
								<Label
									htmlFor="activity-title"
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 11,
										fontWeight: 600,
										letterSpacing: "0.18em",
										textTransform: "uppercase",
										color: "var(--color-dashboard-muted)",
									}}
								>
									{t("realtor.leads.detail.activityTitleLabel")}
								</Label>
								<Input
									id="activity-title"
									value={title}
									onChange={(e) => setTitle(e.target.value)}
									maxLength={200}
									style={{
										backgroundColor: "var(--color-dashboard-paper)",
										borderColor: "var(--color-dashboard-border)",
										color: "var(--color-dashboard-text)",
									}}
								/>
							</div>
						)}
						<div className="space-y-1.5">
							<Label
								htmlFor="activity-body"
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 11,
									fontWeight: 600,
									letterSpacing: "0.18em",
									textTransform: "uppercase",
									color: "var(--color-dashboard-muted)",
								}}
							>
								{asMessage
									? t("realtor.leads.detail.whatWasSaid")
									: t("realtor.leads.detail.activityBodyLabel")}
							</Label>
							<Textarea
								id="activity-body"
								value={body}
								onChange={(e) => setBody(e.target.value)}
								rows={3}
								maxLength={5000}
								style={{
									backgroundColor: "var(--color-dashboard-paper)",
									borderColor: "var(--color-dashboard-border)",
									color: "var(--color-dashboard-text)",
								}}
							/>
						</div>
						{(type === "reminder" || type === "viewing") && (
							<div className="space-y-1.5">
								<Label
									htmlFor="activity-scheduled"
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 11,
										fontWeight: 600,
										letterSpacing: "0.18em",
										textTransform: "uppercase",
										color: "var(--color-dashboard-muted)",
									}}
								>
									{t("realtor.leads.detail.activityScheduledLabel")}
								</Label>
								<Input
									id="activity-scheduled"
									type="datetime-local"
									value={scheduledAt}
									onChange={(e) => setScheduledAt(e.target.value)}
									style={{
										backgroundColor: "var(--color-dashboard-paper)",
										borderColor: "var(--color-dashboard-border)",
										color: "var(--color-dashboard-text)",
									}}
								/>
							</div>
						)}
					</div>
					<DialogFooter>
						<StudioButton
							variant="secondary"
							onClick={() => onOpenChange(false)}
						>
							{t("realtor.leads.detail.cancel")}
						</StudioButton>
						<StudioButton onClick={handleSubmit} disabled={isSubmitting}>
							{isSubmitting
								? t("realtor.leads.form.submitting")
								: t("realtor.leads.detail.logActivity")}
						</StudioButton>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<NextStepPrompt target={nextStep} onClose={closeNextStep} />
		</>
	);
}

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

function KeyValue({
	label,
	value,
	sensitive = false,
}: {
	label: string;
	value: React.ReactNode;
	/** Masks the VALUE in session replay. See lib/posthog.ts. */
	sensitive?: boolean;
}) {
	return (
		<div>
			<p
				style={{
					fontFamily: "var(--font-mono)",
					fontSize: 11,
					fontWeight: 600,
					letterSpacing: "0.18em",
					textTransform: "uppercase",
					color: "var(--color-dashboard-muted)",
				}}
			>
				{label}
			</p>
			<p
				data-sensitive={sensitive || undefined}
				style={{
					fontFamily: "var(--font-body)",
					fontSize: 14,
					color: "var(--color-dashboard-text)",
					marginTop: 4,
				}}
			>
				{value}
			</p>
		</div>
	);
}

function formatBudget(min: number | null, max: number | null): string {
	if (min != null && max != null) {
		return `${formatPaiseAsRupees(min)} – ${formatPaiseAsRupees(max)}`;
	}
	if (min != null) return `≥ ${formatPaiseAsRupees(min)}`;
	if (max != null) return `≤ ${formatPaiseAsRupees(max)}`;
	return "—";
}
